import { cn } from '@/lib/utils';

/**
 * F-500 2026 skeleton primitives for the admin surface.
 *
 * One source of truth replaces the 60+ ad-hoc loaders we used to ship
 * (mix of `animate-pulse`, `Loader2` spinners, custom skeleton classes).
 * Rationale: a consistent skeleton language reads as polish — Stripe,
 * Linear, Vercel all enforce a single shimmer pattern across surfaces.
 *
 * Visual: subtle 1.4s horizontal sweep on a gray-100 base, falls back
 * to a static surface under `prefers-reduced-motion`. Geometry mirrors
 * the real content so there's no layout jump when data lands.
 *
 * Compose larger scaffolds from these primitives. Do NOT introduce new
 * shimmer keyframes elsewhere — extend this file instead.
 */

interface SkeletonBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tailwind sizing classes (height/width/rounded). */
  className?: string;
}

/**
 * Base building block. Wrap with sizing classes (`h-4 w-32 rounded-md`)
 * or one of the named variants below.
 */
export function SkeletonBlock({ className, ...rest }: SkeletonBlockProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('eb-skeleton rounded-md', className)}
      {...rest}
    />
  );
}

/**
 * Text-line skeleton. Use multiple stacked with shrinking widths to
 * simulate paragraphs. Default reads as a single body-line.
 */
export function SkeletonText({
  className,
  width = 'w-full',
  height = 'h-3.5',
}: {
  className?: string;
  width?: string;
  height?: string;
}) {
  return <SkeletonBlock className={cn(height, width, className)} />;
}

/**
 * Title-line skeleton — taller + bolder to match a section heading.
 */
export function SkeletonTitle({
  className,
  width = 'w-48',
}: {
  className?: string;
  width?: string;
}) {
  return <SkeletonBlock className={cn('h-6', width, className)} />;
}

/**
 * Stats grid — mirrors `<StatsCardGrid>` exactly so the swap is
 * imperceptible. Four cards on desktop, two on tablet, stacked on
 * mobile.
 */
export function SkeletonStatsGrid({ columns = 4 }: { columns?: 2 | 3 | 4 } = {}) {
  const colClass =
    columns === 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : columns === 3
        ? 'sm:grid-cols-3'
        : 'sm:grid-cols-2';
  return (
    <div className={cn('grid gap-4 sm:gap-6', colClass)}>
      {Array.from({ length: columns }, (_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-gray-200/80 bg-white p-5"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-3">
              <SkeletonText height="h-3" width="w-1/2" />
              <SkeletonBlock className="h-8 w-2/3" />
              <SkeletonText height="h-3" width="w-3/4" />
            </div>
            <SkeletonBlock className="h-10 w-10 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Card placeholder — generic content surface. Pass rowCount to scale
 * the inner text-lines (default 3).
 */
export function SkeletonCard({
  rowCount = 3,
  className,
  showTitle = true,
}: {
  rowCount?: number;
  className?: string;
  showTitle?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-200/80 bg-white p-5',
        className
      )}
    >
      {showTitle && (
        <div className="mb-4 flex items-center gap-3">
          <SkeletonBlock className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <SkeletonTitle width="w-40" />
            <SkeletonText height="h-3" width="w-56" />
          </div>
        </div>
      )}
      <div className="space-y-2.5">
        {Array.from({ length: rowCount }, (_, i) => (
          <SkeletonText
            key={i}
            width={i === rowCount - 1 ? 'w-2/3' : 'w-full'}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * List skeleton — repeated rows with an avatar slot + two lines of
 * text. Matches the booking-list / employee-list layouts.
 */
export function SkeletonList({
  rowCount = 5,
  className,
}: {
  rowCount?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-gray-200/80 bg-white',
        className
      )}
    >
      {Array.from({ length: rowCount }, (_, i) => (
        <div
          key={i}
          className={cn(
            'flex items-center gap-3 p-4',
            i !== rowCount - 1 && 'border-b border-gray-100'
          )}
        >
          <SkeletonBlock className="h-10 w-10 flex-shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <SkeletonText height="h-3.5" width="w-2/5" />
            <SkeletonText height="h-3" width="w-3/5" />
          </div>
          <SkeletonBlock className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
}

/**
 * Form skeleton — section title + N labeled inputs. Matches the
 * settings form layouts.
 */
export function SkeletonForm({
  fieldCount = 4,
  className,
}: {
  fieldCount?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-gray-200/80 bg-white p-6',
        className
      )}
    >
      <div className="mb-6 space-y-2">
        <SkeletonTitle width="w-56" />
        <SkeletonText height="h-3.5" width="w-80" />
      </div>
      <div className="space-y-5">
        {Array.from({ length: fieldCount }, (_, i) => (
          <div key={i} className="space-y-2">
            <SkeletonText height="h-3" width="w-28" />
            <SkeletonBlock className="h-10 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Page-hero skeleton — title + subtitle + action-row. Matches the
 * AdminPageHeader output.
 */
export function SkeletonPageHero({
  withActions = true,
}: { withActions?: boolean } = {}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-2.5">
        <SkeletonBlock className="h-8 w-72" />
        <SkeletonText height="h-3.5" width="w-96" />
      </div>
      {withActions && (
        <div className="flex flex-shrink-0 gap-2">
          <SkeletonBlock className="h-10 w-32 rounded-lg" />
          <SkeletonBlock className="h-10 w-28 rounded-lg" />
        </div>
      )}
    </div>
  );
}

/**
 * Full-page scaffold for the admin dashboard variant — hero +
 * stats grid + content card. Used by `/admin/loading.tsx` and the
 * in-component loading branch of `/admin/page.tsx`.
 */
export function SkeletonAdminDashboard() {
  return (
    <div className="space-y-6 sm:space-y-8">
      <SkeletonPageHero />
      <SkeletonStatsGrid columns={4} />
      <div className="grid gap-4 lg:grid-cols-3">
        <SkeletonCard className="lg:col-span-2" rowCount={5} />
        <SkeletonCard rowCount={3} />
      </div>
    </div>
  );
}

/**
 * Full-page scaffold for settings — hero + form. The settings page
 * also has a sub-nav above the form which lives in its own layout,
 * so we don't double-render it here.
 */
export function SkeletonSettingsPage({
  fieldCount = 6,
}: { fieldCount?: number } = {}) {
  return (
    <div className="space-y-6 sm:space-y-8">
      <SkeletonPageHero withActions={false} />
      <SkeletonForm fieldCount={fieldCount} />
    </div>
  );
}

/**
 * Full-page scaffold for list-style routes (bookings, employees,
 * services). Hero + filter bar + list rows.
 */
export function SkeletonListPage({
  rowCount = 6,
}: { rowCount?: number } = {}) {
  return (
    <div className="space-y-6 sm:space-y-8">
      <SkeletonPageHero />
      <div className="flex flex-wrap gap-2">
        <SkeletonBlock className="h-10 w-40 rounded-lg" />
        <SkeletonBlock className="h-10 w-32 rounded-lg" />
        <SkeletonBlock className="h-10 w-36 rounded-lg" />
      </div>
      <SkeletonList rowCount={rowCount} />
    </div>
  );
}

/**
 * Full-page scaffold for the calendar route. Sidebar of mini-controls
 * + 7-column day grid.
 */
export function SkeletonCalendarPage() {
  return (
    <div className="space-y-6 sm:space-y-8">
      <SkeletonPageHero />
      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <SkeletonCard rowCount={4} />
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4">
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, i) => (
              <SkeletonText key={`hdr-${i}`} height="h-4" width="w-full" />
            ))}
            {Array.from({ length: 35 }, (_, i) => (
              <SkeletonBlock key={`cell-${i}`} className="h-20 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
